import React from 'react';
import { priceFormatter } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-design-system-ooux-interfaces';

import { formatDeliveryDate } from './formatDeliveryDate';
import type { ProductCardDelivery as Delivery } from './types';
import * as styles from './ProductCardDelivery.styles';

interface ProductCardDeliveryProps {
  currencyCode: CurrencyCode;
  delivery: Delivery;
}

/**
 * The estimated delivery date and its cost.
 *
 * Renders nothing when the date cannot be formatted, on the grounds that a
 * delivery estimate with no date tells the customer nothing, and a broken date
 * is worse than no line at all.
 */
export const ProductCardDelivery = ({ currencyCode, delivery }: ProductCardDeliveryProps) => {
  const { cost, date, eventName } = delivery;
  const formattedDate = formatDeliveryDate(date);

  if (!formattedDate) {
    return null;
  }

  return (
    <p css={styles.delivery} data-testid="product-card-delivery">
      <span css={eventName ? styles.eventLabel : styles.label}>
        {eventName ? `Est to arrive by ${eventName}` : 'Estimated delivery'}
      </span>
      <span>
        <span css={styles.date}>{formattedDate}</span>
        {' · '}
        {cost === 0 ? <span css={styles.free}>FREE</span> : priceFormatter(currencyCode, cost)}
      </span>
    </p>
  );
};

ProductCardDelivery.displayName = 'ProductCardDelivery';
